function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * The IANA timezone of the booking attendee (the student the slots are for),
 * resolved from the authenticated user tree: the user themselves, one of their
 * dependants, their parent, or a sibling (a child of that parent). Falls back
 * to the user's own timezone, then "UTC".
 *
 * This mirrors the timezone the backend actually books in (users.timezone),
 * so any client-side date rule that must line up with the STUDENT's calendar
 * — e.g. the booking date-picker's earliest selectable date — resolves it
 * through here instead of relying on the device timezone.
 */
export function resolveAttendeeTimezone(user: unknown, attendeeId: string): string {
  if (!attendeeId || !isRecord(user)) {
    return "UTC";
  }

  // The logged-in user themselves.
  if (attendeeId === String(user.id)) {
    return (user.timezone as string) || "UTC";
  }

  // One of the user's direct dependants.
  const directDeps = Array.isArray(user.dependants) ? user.dependants : [];
  const directMatch = directDeps.find(
    (d) => isRecord(d) && String((d as Record<string, unknown>).id) === attendeeId,
  );
  if (directMatch && isRecord(directMatch)) {
    return (
      ((directMatch as Record<string, unknown>).timezone as string) ||
      (user.timezone as string) ||
      "UTC"
    );
  }

  // The parent (when the logged-in user is itself a sub-account).
  const parent = isRecord(user.parent) ? user.parent : null;
  if (parent && String((parent as Record<string, unknown>).id) === attendeeId) {
    return ((parent as Record<string, unknown>).timezone as string) || "UTC";
  }

  // A sibling (another child of the parent).
  if (parent) {
    const siblings = Array.isArray((parent as Record<string, unknown>).dependants)
      ? ((parent as Record<string, unknown>).dependants as unknown[])
      : [];
    const sibling = siblings.find(
      (s) => isRecord(s) && String((s as Record<string, unknown>).id) === attendeeId,
    );
    if (sibling && isRecord(sibling)) {
      return ((sibling as Record<string, unknown>).timezone as string) || "UTC";
    }
  }

  return (user.timezone as string) || "UTC";
}
